/**
 * Logger for MCP Server
 * IMPORTANT: All output goes to stderr to avoid corrupting stdio MCP transport
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: unknown;
}

class Logger {
  private level: LogLevel = 'info';
  private readonly levels: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
  };

  setLevel(level: LogLevel): void {
    this.level = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return this.levels[level] >= this.levels[this.level];
  }

  private formatEntry(level: LogLevel, message: string, data?: unknown): string {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(data !== undefined && { data }),
    };
    return JSON.stringify(entry);
  }

  debug(message: string, data?: unknown): void {
    if (this.shouldLog('debug')) {
      console.error(this.formatEntry('debug', message, data));
    }
  }

  info(message: string, data?: unknown): void {
    if (this.shouldLog('info')) {
      console.error(this.formatEntry('info', message, data));
    }
  }

  warn(message: string, data?: unknown): void {
    if (this.shouldLog('warn')) {
      console.error(this.formatEntry('warn', message, data));
    }
  }

  error(message: string, data?: unknown): void {
    if (this.shouldLog('error')) {
      console.error(this.formatEntry('error', message, data));
    }
  }

  // Tool execution logging
  toolStart(toolName: string, args: unknown): void {
    this.info(`Tool started: ${toolName}`, { args });
  }

  toolEnd(toolName: string, success: boolean, duration?: number): void {
    this.info(`Tool completed: ${toolName}`, { success, duration });
  }

  toolError(toolName: string, error: Error): void {
    this.error(`Tool failed: ${toolName}`, {
      error: error.message,
      stack: error.stack
    });
  }
}

export const logger = new Logger();

// Set log level from environment
const envLevel = process.env.LOG_LEVEL as LogLevel | undefined;
if (envLevel && ['debug', 'info', 'warn', 'error'].includes(envLevel)) {
  logger.setLevel(envLevel);
}
